/**
 * Amenity pictograms, drawn with canvas 2D paths.
 *
 * A dot on a map says "something is here". Wayfinding needs "a LIFT is here" —
 * the whole reason a visitor looks at the amenity layer is to find one specific
 * kind of thing, and a legend they have to cross-reference defeats it.
 *
 * Drawn rather than shipped as image assets, for three reasons:
 *
 *   ONE SOURCE.  The importer's review overlay and the 3D scene must show the
 *                same glyph, or confirming a marker on the drawing and finding
 *                it on the map are two different jobs. Both call in here.
 *   ANY SIZE.    A path is resolution-free. The scene rasterises at device
 *                pixel ratio and the overlay at whatever the canvas is, with no
 *                sprite sheet to pick a size from.
 *   PORTABLE.    No imports, no assets, no font files beyond the system stack.
 *                Copyable into another project as one file (see
 *                Docs/mall-map-standalone-module.md).
 *
 * Every symbol draws inside a `size` x `size` box at the origin, in the caller's
 * current strokeStyle / fillStyle. Nothing here sets a colour: the caller owns
 * that, because the 3D scene tints by family and the importer tints by whether
 * the marker has been confirmed yet.
 */

/**
 * Vertical circulation. These are the only amenities that change what a route
 * can DO rather than merely what it passes, so callers colour them apart.
 */
export const VERTICAL_TYPES = new Set(['Escalator', 'Lift', 'Stairs']);

const FONT = 'Inter, system-ui, sans-serif';

/** Centred glyph letter — used where a letter IS the international symbol. */
function letter(ctx, s, ch, weight = 700) {
    ctx.save();
    ctx.font = `${weight} ${s * 0.62}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // 0.04 nudges off the typographic centre onto the optical one; cap-height
    // glyphs sit high in their em box and read as floating without it.
    ctx.fillText(ch, s / 2, (s / 2) + (s * 0.04));
    ctx.restore();
}

function poly(ctx, pts, close = false) {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    if (close) ctx.closePath();
}

/** A stick figure: head, body, arms, legs. Shared by the people-based glyphs. */
function figure(ctx, s, cx, top, h, skirt) {
    const head = h * 0.15;
    ctx.beginPath();
    ctx.arc(cx, top + head, head, 0, Math.PI * 2);
    ctx.fill();

    const shoulder = top + (head * 2.4);
    const hip = top + (h * 0.6);
    if (skirt) {
        poly(ctx, [[cx - (h * 0.28), hip], [cx + (h * 0.28), hip], [cx + (h * 0.08), shoulder], [cx - (h * 0.08), shoulder]], true);
        ctx.fill();
    } else {
        ctx.lineWidth = Math.max(1, h * 0.13);
        poly(ctx, [[cx, shoulder], [cx, hip]]);
        ctx.stroke();
    }
    ctx.lineWidth = Math.max(1, h * 0.11);
    poly(ctx, [[cx - (h * 0.22), shoulder + (h * 0.14)], [cx, shoulder + (h * 0.02)], [cx + (h * 0.22), shoulder + (h * 0.14)]]);
    ctx.stroke();
    poly(ctx, [[cx - (h * 0.15), top + h], [cx, hip], [cx + (h * 0.15), top + h]]);
    ctx.stroke();
}

/** Solid triangle. dir -1 points up, +1 points down. */
function arrowhead(ctx, cx, cy, r, dir) {
    poly(ctx, [[cx - r, cy - (r * dir * 0.7)], [cx + r, cy - (r * dir * 0.7)], [cx, cy + (r * dir * 0.9)]], true);
    ctx.fill();
}

const SYMBOLS = {
    /* An inclined band with treads, and an arrow along it. The incline is what
     * separates it from Stairs at a glance; the arrow says it moves. */
    Escalator(ctx, s) {
        ctx.lineWidth = s * 0.07;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        // The band: two parallel diagonals with a landing at each end.
        poly(ctx, [[s * 0.1, s * 0.84], [s * 0.32, s * 0.84], [s * 0.78, s * 0.3], [s * 0.92, s * 0.3]]);
        ctx.stroke();
        poly(ctx, [[s * 0.1, s * 0.66], [s * 0.22, s * 0.66], [s * 0.66, s * 0.14], [s * 0.92, s * 0.14]]);
        ctx.stroke();
        // Travelling up, drawn INSIDE the band so it cannot read as a stray mark.
        ctx.save();
        ctx.translate(s * 0.5, s * 0.5);
        ctx.rotate(-Math.PI * 0.22);
        poly(ctx, [[-s * 0.13, s * 0.05], [s * 0.09, s * 0.05], [s * 0.09, s * 0.12],
            [s * 0.22, 0], [s * 0.09, -s * 0.12], [s * 0.09, -s * 0.05], [-s * 0.13, -s * 0.05]], true);
        ctx.fill();
        ctx.restore();
    },

    /* A car between shaft walls, with both arrows: a lift goes two ways and is
     * called, which is what distinguishes it from an escalator. */
    Lift(ctx, s) {
        ctx.lineWidth = s * 0.075;
        ctx.strokeRect(s * 0.2, s * 0.14, s * 0.6, s * 0.72);
        arrowhead(ctx, s * 0.38, s * 0.36, s * 0.11, -1);
        arrowhead(ctx, s * 0.62, s * 0.64, s * 0.11, 1);
    },

    /* Bare treads, no incline band and no arrow — you climb these yourself. */
    Stairs(ctx, s) {
        ctx.lineWidth = s * 0.075;
        ctx.lineJoin = 'miter';
        const steps = 4;
        const pts = [[s * 0.14, s * 0.84]];
        for (let i = 0; i < steps; i++) {
            const x = (s * 0.14) + ((s * 0.72 / steps) * i);
            const y = (s * 0.84) - ((s * 0.68 / steps) * i);
            pts.push([x, y], [x + (s * 0.72 / steps), y], [x + (s * 0.72 / steps), y - (s * 0.68 / steps)]);
        }
        poly(ctx, pts);
        ctx.stroke();
    },

    Restrooms(ctx, s) {
        figure(ctx, s, s * 0.31, s * 0.14, s * 0.72, false);
        figure(ctx, s, s * 0.69, s * 0.14, s * 0.72, true);
    },

    /* A mihrab arch — the one element every prayer-room sign shares, and the
     * only one that survives being drawn 20px wide. */
    'Prayer Room'(ctx, s) {
        ctx.lineWidth = s * 0.075;
        ctx.beginPath();
        ctx.moveTo(s * 0.26, s * 0.86);
        ctx.lineTo(s * 0.26, s * 0.44);
        ctx.arc(s * 0.5, s * 0.44, s * 0.24, Math.PI, 0);
        ctx.lineTo(s * 0.74, s * 0.86);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(s * 0.5, s * 0.6, s * 0.07, 0, Math.PI * 2);
        ctx.fill();
    },

    /* A feeding bottle: body, neck, teat. Reads as infant care without needing
     * a recognisable baby, which small pictograms never manage. */
    'Baby Care'(ctx, s) {
        ctx.lineWidth = s * 0.07;
        ctx.beginPath();
        ctx.moveTo(s * 0.34, s * 0.4);
        ctx.lineTo(s * 0.34, s * 0.78);
        ctx.quadraticCurveTo(s * 0.34, s * 0.86, s * 0.44, s * 0.86);
        ctx.lineTo(s * 0.56, s * 0.86);
        ctx.quadraticCurveTo(s * 0.66, s * 0.86, s * 0.66, s * 0.78);
        ctx.lineTo(s * 0.66, s * 0.4);
        ctx.closePath();
        ctx.stroke();
        poly(ctx, [[s * 0.38, s * 0.4], [s * 0.62, s * 0.4]]);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(s * 0.44, s * 0.4);
        ctx.quadraticCurveTo(s * 0.5, s * 0.16, s * 0.56, s * 0.4);
        ctx.fill();
        poly(ctx, [[s * 0.38, s * 0.6], [s * 0.62, s * 0.6]]);
        ctx.lineWidth = s * 0.05;
        ctx.stroke();
    },

    Information(ctx, s) {
        ctx.lineWidth = s * 0.075;
        ctx.beginPath();
        ctx.arc(s * 0.5, s * 0.5, s * 0.36, 0, Math.PI * 2);
        ctx.stroke();
        letter(ctx, s, 'i', 800);
    },

    /* A doorway with an arrow going THROUGH it — direction is the whole point of
     * an entrance marker on a wayfinding map. */
    Entrance(ctx, s) {
        ctx.lineWidth = s * 0.075;
        poly(ctx, [[s * 0.62, s * 0.14], [s * 0.82, s * 0.14], [s * 0.82, s * 0.86], [s * 0.62, s * 0.86]]);
        ctx.stroke();
        poly(ctx, [[s * 0.16, s * 0.5], [s * 0.56, s * 0.5]]);
        ctx.stroke();
        poly(ctx, [[s * 0.44, s * 0.34], [s * 0.62, s * 0.5], [s * 0.44, s * 0.66]], true);
        ctx.fill();
    },

    Parking(ctx, s) {
        ctx.lineWidth = s * 0.075;
        const r = s * 0.12;
        ctx.beginPath();
        ctx.moveTo(s * 0.16 + r, s * 0.14);
        ctx.arcTo(s * 0.84, s * 0.14, s * 0.84, s * 0.86, r);
        ctx.arcTo(s * 0.84, s * 0.86, s * 0.16, s * 0.86, r);
        ctx.arcTo(s * 0.16, s * 0.86, s * 0.16, s * 0.14, r);
        ctx.arcTo(s * 0.16, s * 0.14, s * 0.84, s * 0.14, r);
        ctx.closePath();
        ctx.stroke();
        letter(ctx, s, 'P');
    },

    /* A card going into a slot. A banknote reads as "bureau de change" and a
     * coin as "vending"; the card is what says cash machine. */
    ATM(ctx, s) {
        ctx.lineWidth = s * 0.07;
        ctx.strokeRect(s * 0.16, s * 0.28, s * 0.68, s * 0.44);
        ctx.fillRect(s * 0.16, s * 0.36, s * 0.68, s * 0.1);
        poly(ctx, [[s * 0.24, s * 0.6], [s * 0.44, s * 0.6]]);
        ctx.lineWidth = s * 0.06;
        ctx.stroke();
    },

    'First Aid'(ctx, s) {
        const t = s * 0.2;
        ctx.fillRect((s - t) / 2, s * 0.16, t, s * 0.68);
        ctx.fillRect(s * 0.16, (s - t) / 2, s * 0.68, t);
    },

    /* A lit cigarette. Used for BOTH a smoking area and, tinted differently by
     * the caller, a no-smoking zone — the glyph is the same object either way. */
    'Smoking Area'(ctx, s) {
        ctx.fillRect(s * 0.14, s * 0.56, s * 0.52, s * 0.14);
        ctx.fillRect(s * 0.7, s * 0.56, s * 0.16, s * 0.14);
        ctx.lineWidth = s * 0.06;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(s * 0.56, s * 0.44);
        ctx.quadraticCurveTo(s * 0.68, s * 0.34, s * 0.56, s * 0.24);
        ctx.quadraticCurveTo(s * 0.46, s * 0.16, s * 0.58, s * 0.1);
        ctx.stroke();
    },
};

/**
 * An untyped marker. The detector deliberately proposes markers it cannot
 * classify (see Importer/amenities.js — telling a wheelchair from a prayer mat
 * needs a classifier that does not exist here), and those must look UNRESOLVED
 * rather than like some twelfth facility. A question mark is the honest glyph.
 */
function unknown(ctx, s) {
    ctx.lineWidth = s * 0.06;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.36, 0, Math.PI * 2);
    ctx.stroke();
    letter(ctx, s, '?', 800);
}

/** Is there a real pictogram for this type? */
export const hasSymbol = (type) => !!SYMBOLS[type];

/**
 * Draw `type`'s pictogram into `ctx`, inside a `size` x `size` box whose
 * top-left is the current origin. Falls back to the unresolved glyph.
 *
 * Saves and restores, because every symbol sets lineWidth/lineCap/lineJoin and
 * a caller drawing a hundred markers in a loop must not inherit the last one's.
 */
export function drawAmenitySymbol(ctx, type, size) {
    ctx.save();
    (SYMBOLS[type] || unknown)(ctx, size);
    ctx.restore();
}

/**
 * A standalone canvas holding one pictogram, ready to become a texture.
 *
 * @param {string} type   amenity type, or null/unknown for the unresolved glyph
 * @param {number} px     CSS-pixel side length
 * @param {object} o      { color, dpr, background, ring }
 */
export function amenitySymbolCanvas(type, px, o = {}) {
    const dpr = o.dpr || 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(px * dpr);
    canvas.height = Math.ceil(px * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    if (o.background) {
        ctx.beginPath();
        ctx.arc(px / 2, px / 2, (px / 2) - 1, 0, Math.PI * 2);
        ctx.fillStyle = o.background;
        ctx.fill();
        if (o.ring) {
            ctx.lineWidth = Math.max(1, px * 0.055);
            ctx.strokeStyle = o.ring;
            ctx.stroke();
        }
    }

    const color = o.color || '#F8FAFC';
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    // Inset so the glyph clears the disc's edge and its own stroke width.
    const inset = px * 0.2;
    ctx.translate(inset, inset);
    drawAmenitySymbol(ctx, type, px - (inset * 2));
    return canvas;
}
